import React, { useState, useEffect } from "react"
import { BLOCKS, MARKS } from "@contentful/rich-text-types"
import {
  CuricullamContainer,
  CurriculumListContainer,
  ImgCover,
  List,
  OverviewContainer,
  Title,
  TypographyDescription,
  ContentWrapper,
  TableWrapper,
  GoToTopButton,
  AmountCell,
  FeeChip,
} from "./styles"

import {
  ContentfulRichTextGatsbyReference,
  renderRichText,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"

import { StaticImage } from "gatsby-plugin-image"

interface CourseProps {
  isCertificate: boolean
  courseContent: RenderRichTextData<ContentfulRichTextGatsbyReference>
  lessons: Array<string>
  days: string
  hoursPerDay: string
  assessmentHours: string
  certificates: Array<string>
  hasFunding: string
  price: string
  funding: Array<string>
  assessment: Array<string>
  requirements: Array<string>
  projectImplementation: Array<string>
  courseRelevanceToEmployment: Array<string>
}

const options = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => <span className="bold">{text}</span>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: any, children: any) => <p>{children}</p>,
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      return (
        <>
          <h2>Embedded Asset</h2>
          <pre>
            <code>{JSON.stringify(node, null, 2)}</code>
          </pre>
        </>
      )
    },
  },
  renderText: (text: string) =>
    text.split("\n").reduce<(React.ReactNode | string)[]>((children, seg, i) => {
      return [...children, i > 0 && <br key={i} />, seg]
    }, []),
}

const toNum = (v: unknown): number => {
  const n = typeof v === "number" ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
const fmtSGD = (n: unknown): string =>
  `S$${toNum(n).toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const ItemDescription = (props: CourseProps) => {
  const {
    isCertificate,
    courseContent,
    lessons,
    courseRelevanceToEmployment,
    days,
    hoursPerDay,
    assessmentHours,
    certificates,
    hasFunding,
    price,
    funding,
    assessment,
    requirements,
    projectImplementation,
  } = props

  const priceN = toNum(price)
  const daysN = toNum(days)
  const hoursPerDayN = toNum(hoursPerDay)
  const assessHoursN = toNum(assessmentHours)
  const hf = (hasFunding ?? "").trim().toLowerCase()
  const showNettFees = hf !== "no"
  const totalHours = daysN * hoursPerDayN
  const classroomPracticalHours = Math.max(totalHours - assessHoursN, 0)

  const richText = () => (
    <div>{courseContent ? renderRichText(courseContent, options) : null}</div>
  )

  const [showButton, setShowButton] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShowButton(window.pageYOffset > 300)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" })

  return (
    <div>
      <ContentWrapper>
        <OverviewContainer>
          <Title>{isCertificate ? "Certification" : "Course"} Overview</Title>
          <TypographyDescription>
            {isCertificate
              ? "This credential helps organizations identify and develop talent with critical skills for implementing cloud initiatives. Earning AWS Certified Developer – Associate validates the ability to write and deploy cloud-based applications."
              : richText()}
          </TypographyDescription>
        </OverviewContainer>

        <CuricullamContainer>
          <Title>
            {isCertificate ? "Certification Objectives" : "What you’ll learn"}
          </Title>
          <CurriculumListContainer>
            {isCertificate ? (
              <>
                <List>
                  <ImgCover>
                    <StaticImage
                      src="../../../assets/images/check.png"
                      alt=""
                    />
                  </ImgCover>
                  <p>
                    Demonstrate an understanding of core AWS services, uses, and
                    basic AWS architecture best practices
                  </p>
                </List>
                <List>
                  <ImgCover>
                    <StaticImage
                      src="../../../assets/images/check.png"
                      alt=""
                    />
                  </ImgCover>
                  <p>
                    Demonstrate proficiency in developing, deploying, and
                    debugging cloud-based applications by using AWS
                  </p>
                </List>
              </>
            ) : (
              <>
                {lessons?.length
                  ? lessons.map((lesson, idx) => (
                      <List key={`${lesson}-${idx}`}>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{lesson}</p>
                      </List>
                    ))
                  : null}
              </>
            )}
          </CurriculumListContainer>
        </CuricullamContainer>

        <CuricullamContainer>
          {requirements ? (
            <>
              {courseRelevanceToEmployment?.length > 0 && (
                <>
                  <Title>Why should I attend this Course?</Title>
                  <CurriculumListContainer>
                    {courseRelevanceToEmployment.map((val, idx) => (
                      <List key={`${val}-${idx}`}>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{val}</p>
                      </List>
                    ))}
                  </CurriculumListContainer>
                </>
              )}

              {days?.length > 0 && (
                <>
                  <Title>Duration</Title>
                  <CurriculumListContainer>
                    <List>
                      <ImgCover>
                        <StaticImage
                          src="../../../assets/images/clock.png"
                          alt="duration"
                        />
                      </ImgCover>
                      <p>
                        {daysN} Day(s) × {hoursPerDayN} Hours = {totalHours}{" "}
                        Hours
                        <br />
                        Classroom and Practical: {classroomPracticalHours}{" "}
                        Hours
                        <br />
                        Assessment: {assessHoursN} Hours
                      </p>
                    </List>
                  </CurriculumListContainer>
                </>
              )}

              {certificates?.length > 0 && (
                <>
                  <Title>Certificates</Title>
                  <CurriculumListContainer>
                    {certificates.map((val, idx) => (
                      <List key={`${val}-${idx}`}>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{val}</p>
                      </List>
                    ))}
                  </CurriculumListContainer>
                </>
              )}

              {funding?.length > 0 && (
                <>
                  <Title>Funding</Title>
                  <CurriculumListContainer>
                    {funding.map((val, idx) => (
                      <List key={`${val}-${idx}`}>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{val}</p>
                      </List>
                    ))}
                  </CurriculumListContainer>
                </>
              )}

              {assessment?.length > 0 && (
                <>
                  <Title>Assessment</Title>
                  <CurriculumListContainer>
                    {assessment.map((val, idx) => (
                      <List key={`${val}-${idx}`}>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{val}</p>
                      </List>
                    ))}
                  </CurriculumListContainer>
                </>
              )}

              {requirements?.length > 0 && (
                <>
                  <Title>Entry Requirements</Title>
                  <CurriculumListContainer>
                    {requirements.map((req, idx) => (
                      <List key={`${req}-${idx}`}>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{req}</p>
                      </List>
                    ))}
                  </CurriculumListContainer>
                </>
              )}

              {projectImplementation?.length > 0 && (
                <>
                  <Title>Project Implementation</Title>
                  <CurriculumListContainer>
                    {projectImplementation.map((val, idx) => (
                      <List key={`${val}-${idx}`}>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{val}</p>
                      </List>
                    ))}
                  </CurriculumListContainer>
                </>
              )}
            </>
          ) : null}
        </CuricullamContainer>

        {/* ======== FEES TABLE (stacked on mobile, no rowSpan) ======== */}
        <CuricullamContainer id="course-fee-section">
          <Title>Course Fees</Title>
          <TableWrapper>
            <table>
              <thead>
                <tr>
                  <th>Fee Category</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
              {/* Full course fee — put the price in Amount, leave Description empty */}
              <tr>
                <td data-label="Fee Category">
                  <strong>
                    Full Course Fee 
                  </strong>
                </td>
                <td data-label="Description"></td>
                <AmountCell data-label="Amount"> <strong> {fmtSGD(priceN)} </strong> <small><em>(full price)</em></small> </AmountCell>
              </tr>

              {showNettFees && (
                <>
                  {/* BEFORE GST */}
                  <tr>
                    <td data-label="Fee Category">
                      <strong>
                        Nett Course Fee <FeeChip $tone="before">(before GST)</FeeChip>
                      </strong>
                    </td>
                    <td data-label="Description">Singapore Citizens (SCs) and PR</td>
                    <AmountCell data-label="Amount">
                      <strong> {fmtSGD(priceN * 0.5)} </strong> <small><em>(50% of course fees)</em></small>
                    </AmountCell>
                  </tr>
                  <tr>
                    <td data-label="Fee Category" className="dupCategory">
                      <strong>
                        Nett Course Fee <FeeChip $tone="before">(before GST)</FeeChip>
                      </strong>
                    </td>
                    <td data-label="Description">
                      SCs aged 40 years and above <br /> SME company-sponsored
                    </td>
                    <AmountCell data-label="Amount">
                      <strong> {fmtSGD(priceN * 0.3)} </strong> <small><em>(30% of course fees)</em></small>
                    </AmountCell>
                  </tr>

                  {/* AFTER GST — first row shows the category, the next row hides the
                      repeated label on desktop only (kept for mobile cards). */}
                  <tr>
                    <td data-label="Fee Category">
                      <strong>
                        Nett Course Fee <FeeChip $tone="after">(after GST)</FeeChip>
                      </strong>
                    </td>
                    <td data-label="Description">Singapore Citizens (SCs) and PR</td>
                    <AmountCell data-label="Amount">
                      <strong> {fmtSGD(priceN * 0.59)} </strong> <small><em>(50% + GST)</em></small>
                    </AmountCell>
                  </tr>
                  <tr>
                    <td data-label="Fee Category" className="dupCategory">
                      <strong>
                        Nett Course Fee <FeeChip $tone="after">(after GST)</FeeChip>
                      </strong>
                    </td>
                    <td data-label="Description">
                      SCs aged 40 years and above <br /> SME company-sponsored
                    </td>
                    <AmountCell data-label="Amount">
                      <strong> {fmtSGD(priceN * 0.39)} </strong> <small><em>(30% + GST)</em></small>
                    </AmountCell>
                  </tr>
                </>
              )}
            </tbody>
            </table>
          </TableWrapper>
        </CuricullamContainer>

        {showButton && (
          <GoToTopButton onClick={scrollToTop}>Go to Top</GoToTopButton>
        )}
      </ContentWrapper>
    </div>
  )
}

export default ItemDescription